import React from 'react';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import { ContactFormProps, ContactFormData } from '../../types';
import { colors, typography } from '../../styles/designTokens';
import { useScrollAnimation } from '../../utils/useScrollAnimation';

const ContactForm: React.FC<ContactFormProps> = ({ onSubmit, isFormContainerVisible = false }) => {
  const formWrapperRef = useScrollAnimation({ animationType: 'fadeIn', threshold: 0.1, triggerOnce: true });
  const [formData, setFormData] = React.useState<ContactFormData>({
    fullName: '',
    phone: '',
    email: '',
    message: '',
  });
  const [, setIsFormVisible] = React.useState(false);
  const formVisibleRef = React.useRef<HTMLDivElement>(null);
  
  // IntersectionObserver for Staggered Fade-In
  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsFormVisible(true);
        }
      },
      {
        threshold: 0.2,
        rootMargin: '0px',
      }
    );

    const currentRef = formVisibleRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);
  const [termsAccepted, setTermsAccepted] = React.useState(false);
  const [submitStatus, setSubmitStatus] = React.useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  
  // 🛡️ הגנה חזקה מפני שליחה כפולה - useRef
  const isSubmittingRef = React.useRef(false);
  const lastSubmissionTimeRef = React.useRef(0);
  
  const [snackbar, setSnackbar] = React.useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // 🛡️ הגנה חזקה מפני שליחה כפולה - בדיקה עם useRef
    const now = Date.now();
    
    // אם כבר בשליחה, לא לשלוח שוב
    if (isSubmittingRef.current) {
      return;
    }
    
    // אם הסטטוס הוא 'sending', לא לשלוח שוב
    if (submitStatus === 'sending') {
      return;
    }
    
    // Validation ידני - בלי required ב-HTML
    if (!formData.fullName.trim()) {
      setSnackbar({
        open: true,
        message: 'אנא הזן שם מלא',
        severity: 'warning',
      });
      return;
    }
    
    if (!formData.phone.trim()) {
      setSnackbar({
        open: true,
        message: 'אנא הזן מספר טלפון',
        severity: 'warning',
      });
      return;
    }
    
    if (!formData.email.trim()) {
      setSnackbar({
        open: true,
        message: 'אנא הזן כתובת אימייל',
        severity: 'warning',
      });
      return;
    }
    
    if (!formData.message.trim()) {
      setSnackbar({
        open: true,
        message: 'אנא הזן הודעה',
        severity: 'warning',
      });
      return;
    }
    
    if (!termsAccepted) {
      setSnackbar({
        open: true,
        message: 'אנא אשר את תנאי השימוש',
        severity: 'warning',
      });
      return;
    }

    // 🛡️ סימון שהטופס בשליחה
    isSubmittingRef.current = true;
    lastSubmissionTimeRef.current = now;
    setSubmitStatus('sending');
    
    try {
      if (onSubmit) {
        await onSubmit(formData);
      }
      
      // Reset form
      setFormData({
        fullName: '',
        phone: '',
        email: '',
        message: '',
      });
      setTermsAccepted(false);
      setSubmitStatus('success');
      
      // Success Animation - אנימציה מיוחדת להצלחה
      const submitButton = document.querySelector('.contact-submit-btn') as HTMLElement;
      if (submitButton) {
        submitButton.style.animation = 'successPulse 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)';
        setTimeout(() => {
          if (submitButton) {
            submitButton.style.animation = '';
          }
        }, 600);
      }
      
      // Show success message
      setSnackbar({
        open: true,
        message: 'הטופס נשלח בהצלחה! ניצור איתך קשר בהקדם.',
        severity: 'success',
      });
      
      // Reset status after 2 seconds
      setTimeout(() => {
        setSubmitStatus('idle');
        isSubmittingRef.current = false;
        lastSubmissionTimeRef.current = 0; // ✅ מנקים את הזמן כדי לאפשר שליחה נוספת
      }, 2000);
      
    } catch (error) {
      setSubmitStatus('error');
      
      // Show error message
      setSnackbar({
        open: true,
        message: 'אירעה שגיאה בשליחת הטופס. אנא נסה שוב מאוחר יותר.',
        severity: 'error',
      });
      
      // Reset status after 3 seconds
      setTimeout(() => {
        setSubmitStatus('idle');
        isSubmittingRef.current = false;
      }, 3000);
    }
  };

  return (
    <>
      <div ref={formWrapperRef} className="contact-form-wrapper">
        <div ref={formVisibleRef}>
          <form onSubmit={handleSubmit} className="contact-form flex flex-col gap-6">
        {/* Full Name and Phone - Two columns */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Full Name - MUI TextField without Floating Label */}
          <TextField
            id="fullName"
            name="fullName"
            value={formData.fullName}
            onChange={handleInputChange}
            placeholder="שם מלא"
            fullWidth
            variant="outlined"
            aria-label="שם מלא"
            InputLabelProps={{
              shrink: false,
            }}
            sx={{
              opacity: isFormContainerVisible ? 1 : 0,
              transform: isFormContainerVisible ? 'translateY(0)' : 'translateY(20px)',
              transition: 'opacity 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.3s, transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.3s',
              '& .MuiOutlinedInput-root': {
                fontSize: typography.fontSize.base,
                backgroundColor: '#000000 !important', // רקע שחור תמיד
                color: '#FFFFFF !important', // טקסט לבן
                '& input': {
                  textAlign: 'right',
                  padding: '16px 14px',
                  color: '#FFFFFF !important', // טקסט לבן
                  backgroundColor: 'transparent !important', // רקע שקוף כדי שהרקע של ה-root יראה
                },
                '& input:-webkit-autofill': {
                  WebkitBoxShadow: '0 0 0 1000px #000000 inset !important',
                  WebkitTextFillColor: '#FFFFFF !important',
                  backgroundColor: '#000000 !important',
                },
                '& input:-webkit-autofill:hover': {
                  WebkitBoxShadow: '0 0 0 1000px #000000 inset !important',
                  WebkitTextFillColor: '#FFFFFF !important',
                  backgroundColor: '#000000 !important',
                },
                '& input:-webkit-autofill:focus': {
                  WebkitBoxShadow: '0 0 0 1000px #000000 inset !important',
                  WebkitTextFillColor: '#FFFFFF !important',
                  backgroundColor: '#000000 !important',
                },
                '& input:-webkit-autofill:active': {
                  WebkitBoxShadow: '0 0 0 1000px #000000 inset !important',
                  WebkitTextFillColor: '#FFFFFF !important',
                  backgroundColor: '#000000 !important',
                },
                '&.Mui-focused': {
                  backgroundColor: '#000000 !important', // רקע שחור גם ב-focus
                },
                '&:hover': {
                  backgroundColor: '#000000 !important', // רקע שחור גם ב-hover
                },
                '&.Mui-filled': {
                  backgroundColor: '#000000 !important', // רקע שחור גם כשמלא
                },
                '& fieldset': {
                  borderColor: formData.fullName ? colors.gold.base : 'rgba(245, 182, 88, 0.3)',
                  transition: 'border-color 0.3s ease',
                },
                '&:hover fieldset': {
                  borderColor: colors.gold.base,
                },
                '&.Mui-focused fieldset': {
                  borderColor: colors.gold.base,
                  borderWidth: '2px',
                },
                '& .MuiInputBase-input': {
                  color: '#FFFFFF !important', // טקסט לבן
                },
              },
              '& .MuiInputLabel-root': {
                display: 'none', // הסרת ה-label
              },
              '& input::placeholder': {
                color: '#FFFFFF !important',
                opacity: 1,
              },
              '& input::-webkit-input-placeholder': {
                color: '#FFFFFF !important',
                opacity: 1,
              },
              '& input::-moz-placeholder': {
                color: '#FFFFFF !important',
                opacity: 1,
              },
              '& input:-ms-input-placeholder': {
                color: '#FFFFFF !important',
                opacity: 1,
              },
              '& .MuiInputBase-input': {
                color: '#FFFFFF !important', // טקסט לבן
              },
            }}
          />

          {/* Phone - MUI TextField without Floating Label */}
          <TextField
            id="phone"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleInputChange}
            placeholder="טלפון"
            fullWidth
            variant="outlined"
            aria-label="מספר טלפון"
            InputLabelProps={{
              shrink: false,
            }}
            InputProps={{
              sx: {
                backgroundColor: '#000000 !important', // רקע שחור תמיד
                color: '#FFFFFF !important', // טקסט לבן
                '& input': {
                  backgroundColor: 'transparent !important', // רקע שקוף
                  color: '#FFFFFF !important', // טקסט לבן
                },
              },
            }}
            sx={{
              opacity: isFormContainerVisible ? 1 : 0,
              transform: isFormContainerVisible ? 'translateY(0)' : 'translateY(20px)',
              transition: 'opacity 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.4s, transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.4s',
              '& .MuiOutlinedInput-root': {
                fontSize: typography.fontSize.base,
                backgroundColor: '#000000 !important', // רקע שחור תמיד
                color: '#FFFFFF !important', // טקסט לבן
                '& input': {
                  textAlign: 'right',
                  padding: '16px 14px',
                  color: '#FFFFFF !important', // טקסט לבן
                  backgroundColor: 'transparent !important', // רקע שקוף כדי שהרקע של ה-root יראה
                },
                '& input:-webkit-autofill': {
                  WebkitBoxShadow: '0 0 0 1000px #000000 inset !important',
                  WebkitTextFillColor: '#FFFFFF !important',
                  backgroundColor: '#000000 !important',
                },
                '& input:-webkit-autofill:hover': {
                  WebkitBoxShadow: '0 0 0 1000px #000000 inset !important',
                  WebkitTextFillColor: '#FFFFFF !important',
                  backgroundColor: '#000000 !important',
                },
                '& input:-webkit-autofill:focus': {
                  WebkitBoxShadow: '0 0 0 1000px #000000 inset !important',
                  WebkitTextFillColor: '#FFFFFF !important',
                  backgroundColor: '#000000 !important',
                },
                '& input:-webkit-autofill:active': {
                  WebkitBoxShadow: '0 0 0 1000px #000000 inset !important',
                  WebkitTextFillColor: '#FFFFFF !important',
                  backgroundColor: '#000000 !important',
                },
                '&.Mui-focused': {
                  backgroundColor: '#000000 !important', // רקע שחור גם ב-focus
                },
                '&:hover': {
                  backgroundColor: '#000000 !important', // רקע שחור גם ב-hover
                },
                '&.Mui-filled': {
                  backgroundColor: '#000000 !important', // רקע שחור גם כשמלא
                },
                '&.MuiOutlinedInput-root.Mui-filled': {
                  backgroundColor: '#000000 !important', // רקע שחור גם כשמלא
                },
                '& fieldset': {
                  borderColor: formData.phone ? colors.gold.base : 'rgba(245, 182, 88, 0.3)',
                  transition: 'border-color 0.3s ease',
                },
                '&:hover fieldset': {
                  borderColor: colors.gold.base,
                },
                '&.Mui-focused fieldset': {
                  borderColor: colors.gold.base,
                  borderWidth: '2px',
                },
                '& .MuiInputBase-input': {
                  color: '#FFFFFF !important', // טקסט לבן
                },
              },
              '& .MuiInputLabel-root': {
                display: 'none', // הסרת ה-label
              },
              '& input::placeholder': {
                color: '#FFFFFF !important',
                opacity: 1,
              },
              '& input::-webkit-input-placeholder': {
                color: '#FFFFFF !important',
                opacity: 1,
              },
              '& input::-moz-placeholder': {
                color: '#FFFFFF !important',
                opacity: 1,
              },
              '& input:-ms-input-placeholder': {
                color: '#FFFFFF !important',
                opacity: 1,
              },
              '& .MuiInputBase-input': {
                color: '#FFFFFF !important', // טקסט לבן
              },
            }}
          />
        </div>
          
        {/* Email - MUI TextField without Floating Label */}
        <TextField
          id="email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleInputChange}
          placeholder="איימיל"
          fullWidth
          variant="outlined"
          aria-label="כתובת אימייל"
          InputLabelProps={{
            shrink: false,
          }}
          InputProps={{
            sx: {
              backgroundColor: '#000000 !important', // רקע שחור תמיד
              '& input': {
                backgroundColor: 'transparent !important', // רקע שקוף
              },
            },
          }}
            sx={{
              opacity: isFormContainerVisible ? 1 : 0,
              transform: isFormContainerVisible ? 'translateY(0)' : 'translateY(20px)',
              transition: 'opacity 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.5s, transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.5s',
              '& .MuiOutlinedInput-root': {
                fontSize: typography.fontSize.base,
                backgroundColor: '#000000 !important', // רקע שחור תמיד
                color: '#FFFFFF !important', // טקסט לבן
                '& input': {
                  textAlign: 'right',
                  padding: '16px 14px',
                  color: '#FFFFFF !important', // טקסט לבן תמיד
                  backgroundColor: 'transparent !important', // רקע שקוף כדי שהרקע של ה-root יראה
                },
                '& input:-webkit-autofill': {
                  WebkitBoxShadow: '0 0 0 1000px #000000 inset !important',
                  WebkitTextFillColor: '#FFFFFF !important',
                  backgroundColor: '#000000 !important',
                },
                '& input:-webkit-autofill:hover': {
                  WebkitBoxShadow: '0 0 0 1000px #000000 inset !important',
                  WebkitTextFillColor: '#FFFFFF !important',
                  backgroundColor: '#000000 !important',
                },
                '& input:-webkit-autofill:focus': {
                  WebkitBoxShadow: '0 0 0 1000px #000000 inset !important',
                  WebkitTextFillColor: '#FFFFFF !important',
                  backgroundColor: '#000000 !important',
                },
                '& input:-webkit-autofill:active': {
                  WebkitBoxShadow: '0 0 0 1000px #000000 inset !important',
                  WebkitTextFillColor: '#FFFFFF !important',
                  backgroundColor: '#000000 !important',
                },
                '&.Mui-focused': {
                  backgroundColor: '#000000 !important', // רקע שחור גם ב-focus
                },
                '&:hover': {
                  backgroundColor: '#000000 !important', // רקע שחור גם ב-hover
                },
                '&.Mui-filled': {
                  backgroundColor: '#000000 !important', // רקע שחור גם כשמלא
                },
                '&.MuiOutlinedInput-root.Mui-filled': {
                  backgroundColor: '#000000 !important', // רקע שחור גם כשמלא
                },
                '& fieldset': {
                  borderColor: formData.email ? colors.gold.base : 'rgba(245, 182, 88, 0.3)',
                  transition: 'border-color 0.3s ease',
                },
                '&:hover fieldset': {
                  borderColor: colors.gold.base,
                },
                '&.Mui-focused fieldset': {
                  borderColor: colors.gold.base,
                  borderWidth: '3px',
                  boxShadow: `0 0 0 4px ${colors.gold.mediumLight}30, 0 0 20px ${colors.gold.mediumLight}60`,
                  transition: 'box-shadow 0.3s ease, border-color 0.3s ease',
                },
              },
            '& .MuiInputLabel-root': {
              display: 'none', // הסרת ה-label
            },
            '& input::placeholder': {
              color: '#FFFFFF !important',
              opacity: 1,
            },
            '& input::-webkit-input-placeholder': {
              color: '#FFFFFF !important',
              opacity: 1,
            },
            '& input::-moz-placeholder': {
              color: '#FFFFFF !important',
              opacity: 1,
            },
            '& input:-ms-input-placeholder': {
              color: '#FFFFFF !important',
              opacity: 1,
            },
          }}
        />
          
        {/* Message - MUI TextField multiline without Floating Label */}
        <TextField
          id="message"
          name="message"
          value={formData.message}
          onChange={handleInputChange}
          placeholder="תוכן הפניה"
          fullWidth
          multiline
          rows={5}
          variant="outlined"
          aria-label="תוכן הפניה"
          InputLabelProps={{
            shrink: false,
          }}
          sx={{
            opacity: isFormContainerVisible ? 1 : 0,
            transform: isFormContainerVisible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.6s, transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.6s',
            '& .MuiOutlinedInput-root': {
              fontSize: typography.fontSize.base,
              backgroundColor: '#000000 !important', // רקע שחור תמיד
              color: '#FFFFFF !important', // טקסט לבן
              '& textarea': {
                textAlign: 'right',
                padding: '16px 14px',
                resize: 'none',
                color: '#FFFFFF !important', // טקסט לבן
                backgroundColor: 'transparent !important', // רקע שקוף כדי שהרקע של ה-root יראה
              },
              '& .MuiInputBase-input': {
                color: '#FFFFFF !important', // טקסט לבן
              },
              '& textarea:-webkit-autofill': {
                WebkitBoxShadow: '0 0 0 1000px #000000 inset !important',
                WebkitTextFillColor: '#FFFFFF !important',
                backgroundColor: '#000000 !important',
              },
              '&.Mui-focused': {
                backgroundColor: '#000000 !important', // רקע שחור גם ב-focus
              },
              '&:hover': {
                backgroundColor: '#000000 !important', // רקע שחור גם ב-hover
              },
              '&.Mui-filled': {
                backgroundColor: '#000000 !important', // רקע שחור גם כשמלא
              },
              '& fieldset': {
                borderColor: formData.message ? colors.gold.base : 'rgba(245, 182, 88, 0.3)',
                transition: 'border-color 0.3s ease',
              },
              '&:hover fieldset': {
                borderColor: colors.gold.base,
              },
              '&.Mui-focused fieldset': {
                borderColor: colors.gold.base,
                borderWidth: '2px',
                boxShadow: `0 0 0 3px ${colors.gold.mediumLight}30, 0 0 20px ${colors.gold.mediumLight}60`,
                transition: 'box-shadow 0.3s ease, border-color 0.3s ease',
              },
            },
            '& .MuiInputLabel-root': {
              display: 'none', // הסרת ה-label
            },
            '& textarea::placeholder': {
              color: '#FFFFFF !important',
              opacity: 1,
            },
            '& textarea::-webkit-input-placeholder': {
              color: '#FFFFFF !important',
              opacity: 1,
            },
            '& textarea::-moz-placeholder': {
              color: '#FFFFFF !important',
              opacity: 1,
            },
            '& textarea:-ms-input-placeholder': {
              color: '#FFFFFF !important',
              opacity: 1,
            },
          }}
        />
    
        {/* Consent Checkbox - MUI Checkbox */}
        <FormControlLabel
          control={
            <Checkbox
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              sx={{
                color: colors.orangeGold,
                '&.Mui-checked': {
                  color: colors.orange,
                },
                '& .MuiSvgIcon-root': {
                  fontSize: 20,
                },
              }}
            />
          }
          label={
            <span className="text-sm font-normal leading-normal" style={{ color: colors.gold.base }}>
              אני מאשר/ת את שליחת הטופס ומסכים/ה ל
              <a 
                href="/website-terms" 
                className="underline"
                style={{ color: colors.gold.mediumLight }}
              >
                תנאי השימוש ומדיניות הפרטיות
              </a>
            </span>
          }
          sx={{
            alignItems: 'center',
            marginRight: 0,
            marginLeft: 'auto',
            marginTop: 0,
            marginBottom: 0,
            opacity: isFormContainerVisible ? 1 : 0,
            transform: isFormContainerVisible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.65s, transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.65s',
            '& .MuiFormControlLabel-label': {
              marginRight: '8px',
              marginTop: 0,
              marginBottom: 0,
            },
          }}
        />

        {/* Submit Button with Loading State - MUI styled - עם ripple effect + shimmer */}
        <button
          type="submit"
          disabled={submitStatus === 'sending' || !termsAccepted}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && termsAccepted && submitStatus !== 'sending') {
              e.preventDefault();
              handleSubmit(e as any);
            }
          }}
          aria-label={submitStatus === 'sending' ? 'שולח טופס...' : 'שלח טופס יצירת קשר'}
          className="group contact-submit-btn ripple-button rounded-pill font-bold text-center transition-all duration-300 flex items-center justify-center gap-2 text-lg relative"
          style={{
            background: termsAccepted ? colors.accentSpecial.buttonGradientDiagonal : '#808080', // אפור כשלא מסומן, גרדיאנט כשמסומן
            letterSpacing: typography.body.letterSpacing,
            cursor: (submitStatus === 'sending' || !termsAccepted) ? 'not-allowed' : 'pointer',
            opacity: (submitStatus === 'sending' || !termsAccepted) ? 0.5 : (isFormContainerVisible ? 1 : 0),
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
            transform: isFormContainerVisible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.7s, transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.7s, background 0.3s ease',
            animation: termsAccepted ? 'orderButtonColorSwitch 2s steps(1, end) infinite' : 'none',
            padding: 'clamp(10px, 1.2vw, 12px) clamp(60px, 8vw, 120px)',
            justifyContent: 'center',
            color: '#000000',
            fontSize: 'clamp(18px, 2.2vw, 22px)',
            width: 'auto',
            maxWidth: '400px',
            margin: '0 auto',
            overflow: 'visible',
          }}
        >
          {/* Shimmer Effect */}
          {submitStatus !== 'sending' && termsAccepted && (
            <span 
              className="shimmer-effect absolute inset-0 opacity-0"
              style={{
                background: 'linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.3) 50%, transparent 100%)',
                animation: 'shimmer 3s infinite',
                pointerEvents: 'none',
              }}
            />
          )}
          {submitStatus === 'sending' ? (
            <>
              <CircularProgress size={20} sx={{ color: colors.gold.base }} />
              <span>שולח...</span>
            </>
          ) : (
            <>
              <span>שליחה</span>
              
              {/* עיגול שחור עם חץ כתום - רק כשמסומן */}
              {termsAccepted && (
                <div 
                  className="flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-75 ease-out group-hover:-translate-x-2"
                  style={{
                    position: 'absolute',
                    left: '0px',
                    width: 'clamp(48px, 5.5vw, 58px)',
                    height: 'clamp(48px, 5.5vw, 58px)',
                    backgroundColor: '#000000',
                    border: '2px solid #D4943A',
                  }}
                >
                  {/* אייקון חץ */}
                  <svg 
                    className="transition-transform duration-75 ease-out rotate-45 group-hover:!rotate-0"
                    width="28" 
                    height="28" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    xmlns="http://www.w3.org/2000/svg"
                    style={{
                      color: colors.gold.base,
                    }}
                  >
                    <path 
                      d="M19 12H5M5 12L12 5M5 12L12 19" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              )}
            </>
          )}
        </button>
      </form>
        </div>
      </div>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{
            width: '100%',
            direction: 'rtl',
            '& .MuiAlert-icon': {
              marginLeft: 1,
              marginRight: 0,
            },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ContactForm;

